<script setup>
import { ref, watch, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAbortableRequest } from 'dashboard/composables/useAbortableRequest';
import kanbanClient from 'dashboard/api/kanban/kanbanClient';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  deal: { type: Object, required: true },
  active: { type: Boolean, default: true },
});
const { t } = useI18n();
const { accountId } = useAccount();
const { run, abort, isPending } = useAbortableRequest();
const statistics = ref(null);
const error = ref(false);
const label = key => t(`KANBAN.STATISTICS.${key}`);
const load = async () => {
  statistics.value = null;
  error.value = false;
  await run(async signal => {
    try {
      const { data } = await kanbanClient.getDealStatistics(
        props.deal.kanban_funnel_id,
        props.deal.id,
        { signal }
      );
      if (!signal.aborted) statistics.value = data;
    } catch {
      if (!signal.aborted) error.value = true;
    }
  });
};
watch(
  [
    accountId,
    () => props.deal.id,
    () => props.deal.kanban_funnel_id,
    () => props.deal.kanban_stage_id,
    () => props.deal.lock_version,
    () => props.active,
  ],
  () => {
    abort();
    statistics.value = null;
    error.value = false;
    if (props.active) load();
  },
  { immediate: true }
);
const date = value =>
  value ? new Date(value).toLocaleString() : label('UNKNOWN');
const duration = seconds => {
  if (seconds === null || seconds === undefined) return label('UNKNOWN');
  if (seconds < 60) return label('LESS_THAN_MINUTE');
  if (seconds < 3600)
    return t('KANBAN.STATISTICS.MINUTES', { count: Math.floor(seconds / 60) });
  if (seconds < 86400)
    return t('KANBAN.STATISTICS.HOURS', { count: Math.floor(seconds / 3600) });
  return t('KANBAN.STATISTICS.DAYS', { count: Math.floor(seconds / 86400) });
};
const counters = computed(() => {
  if (!statistics.value) return [];
  const { tasks, activity } = statistics.value;
  return [
    ['PENDING_TASKS', tasks.pending],
    ['OVERDUE_TASKS', tasks.overdue],
    ['COMPLETED_TASKS', tasks.completed],
    ['UNDATED_TASKS', tasks.without_due_date],
    ['CONVERSATIONS', activity.conversations],
    ...Object.entries(activity.by_status).map(([status, count]) => [
      `STATUS_${status.toUpperCase()}`,
      count,
    ]),
    ['INCOMING_MESSAGES', activity.incoming_messages],
    ['OUTGOING_MESSAGES', activity.outgoing_messages],
    ['FAILED_OUTGOING', activity.failed_outgoing],
    ['INTERNAL_NOTES', activity.internal_notes],
    ['EMAIL_MESSAGES', activity.email_messages],
    ['INCOMING_CALLS', activity.calls?.incoming ?? label('UNAVAILABLE')],
    ['OUTGOING_CALLS', activity.calls?.outgoing ?? label('UNAVAILABLE')],
  ];
});
</script>

<template>
  <section
    class="flex flex-col gap-3"
    :aria-label="label('TITLE')"
    :aria-busy="isPending"
  >
    <div class="flex items-center justify-between gap-2">
      <h3 class="m-0 text-sm font-semibold">{{ label('TITLE') }}</h3>
      <Button
        variant="ghost"
        size="sm"
        icon="i-lucide-refresh-cw"
        :disabled="isPending || !active"
        :aria-label="label('REFRESH')"
        @click="load"
      />
    </div>
    <p class="m-0 text-xs text-n-slate-10">{{ label('SCOPE') }}</p>
    <Spinner v-if="isPending" />
    <p v-else-if="error" role="alert" class="text-xs text-n-ruby-11">
      {{ label('ERROR') }}
    </p>
    <template v-else-if="statistics">
      <dl class="m-0 grid grid-cols-2 gap-x-3 gap-y-2 text-xs">
        <dt>{{ label('CREATED_AT') }}</dt>
        <dd class="m-0">{{ date(statistics.created_at) }}</dd>
        <dt>{{ label('AGE') }}</dt>
        <dd class="m-0">{{ duration(statistics.age_seconds) }}</dd>
        <dt>{{ label('STAGE_ENTERED_AT') }}</dt>
        <dd class="m-0">{{ date(statistics.stage_entry.entered_at) }}</dd>
        <dt>{{ label('STAGE_ELAPSED') }}</dt>
        <dd class="m-0">
          {{ duration(statistics.stage_entry.elapsed_seconds) }}
        </dd>
        <dt>{{ label('SOURCE') }}</dt>
        <dd class="m-0 break-words">
          {{ statistics.source || label('UNKNOWN') }}
        </dd>
        <template v-for="[key, value] in counters" :key="key">
          <dt>{{ label(key) }}</dt>
          <dd class="m-0">{{ value }}</dd>
        </template>
      </dl>
      <p class="m-0 text-xs text-n-slate-10">{{ label('COUNT_LIMIT') }}</p>
      <h4 class="m-0 text-xs font-semibold">{{ label('TRACKING') }}</h4>
      <dl
        v-if="Object.keys(statistics.tracking).length"
        class="m-0 grid grid-cols-2 gap-2 text-xs"
      >
        <template v-for="(value, key) in statistics.tracking" :key="key">
          <dt>{{ key }}</dt>
          <dd class="m-0 break-all">{{ value }}</dd>
        </template>
      </dl>
      <p v-else class="m-0 text-xs text-n-slate-10">{{ label('UNKNOWN') }}</p>
      <time :datetime="statistics.as_of" class="text-xs text-n-slate-10">
        {{ t('KANBAN.STATISTICS.AS_OF', { date: date(statistics.as_of) }) }}
      </time>
    </template>
  </section>
</template>
